// ─────────────────────────────────────────────────────────────────────────
// ORPHAN / TERMINAL GUARD PANEL  (System tab)
//
// Surfaces positions/orders that need a MANUAL look (Portfolio-Integrity master
// plan, Part 2). Two kinds:
//   • inactive orphan  — an INACTIVE strategy still holding a position or carrying
//     a pending order PAST its deactivation day (a same-day close-out is excluded).
//   • merged/delisted  — a position in a terminal-halted symbol; the Fix-2 halt
//     preserves it WITHOUT deactivating the strategy, so it needs disposition.
//
// Read-only (a daily beat also emails a digest). Nothing is auto-sold — disposition
// stays a deliberate human action. Mobile-first, dark-default, institutional styling.
// ─────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useState } from 'react';
import { tradingService } from '../../../services/tradingService';

const Spinner = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z" />
  </svg>
);

const qty = (v) => (v == null ? '—' : Number(v).toLocaleString(undefined, { maximumFractionDigits: 4 }));

function KindBadge({ kind }) {
  if (kind === 'terminal_held') {
    return <span className="text-amber-600 dark:text-amber-400 font-semibold">merged / delisted</span>;
  }
  if (kind === 'terminal_parked') {
    return <span className="text-amber-600 dark:text-amber-400 font-semibold">dead stock · sleeve on</span>;
  }
  return <span className="text-red-600 dark:text-red-400 font-semibold">inactive orphan</span>;
}

export default function OrphanGuardPanel() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await tradingService.getOrphanGuard();
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const findings = data?.findings || [];

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#0d1117] p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-sm font-serif text-gray-900 dark:text-gray-100">Orphan / Terminal Guard</h3>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
            Positions/orders needing manual disposition — an inactive strategy lingering past its
            close, a merged/delisted name frozen by the terminal halt, or an active sleeve whose own
            stock is dead (money parked in an ETF until you stop it).
          </p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="text-[11px] px-2 py-1 rounded border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-brand-gold/50 disabled:opacity-50 inline-flex items-center gap-1 shrink-0"
        >
          {loading ? <Spinner /> : '↻'} Refresh
        </button>
      </div>

      {error && <div className="text-[11px] text-red-600 dark:text-red-400 mb-2">Error: {error}</div>}

      {findings.length === 0 ? (
        <div className="text-[11px] text-emerald-600 dark:text-emerald-400 py-2">
          ✓ Clean — no orphaned or merged/delisted positions need attention.
        </div>
      ) : (
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-800">
                <th className="py-1.5 pr-3 text-left font-medium">Type</th>
                <th className="py-1.5 pr-3 text-left font-medium">Strategy</th>
                <th className="py-1.5 pr-3 text-left font-medium">Symbol</th>
                <th className="py-1.5 pr-3 text-right font-medium">Position</th>
                <th className="py-1.5 pr-3 text-right font-medium">Orders</th>
                <th className="py-1.5 pr-3 text-left font-medium">Deactivated</th>
              </tr>
            </thead>
            <tbody>
              {findings.map((f, i) => (
                <tr key={`${f.strategy_id}-${f.symbol || ''}-${i}`} className="border-b border-gray-100 dark:border-gray-800/60">
                  <td className="py-1.5 pr-3"><KindBadge kind={f.kind} /></td>
                  <td className="py-1.5 pr-3 text-gray-700 dark:text-gray-300">
                    {f.strategy_name} <span className="text-gray-400">#{f.strategy_id}</span>
                  </td>
                  <td className="py-1.5 pr-3 font-semibold text-gray-800 dark:text-gray-200">
                    {f.symbol || '—'}
                    {f.kind === 'terminal_parked' && (
                      <span className="block font-normal text-[10px] text-gray-500 dark:text-gray-400">
                        parked in {(f.parked || []).map(p => `${qty(p.qty)} ${p.symbol}`).join(', ') || 'nothing'} · {f.account}
                      </span>
                    )}
                  </td>
                  <td className={`py-1.5 pr-3 text-right tabular-nums ${(f.open_position_qty || 0) !== 0 ? 'text-gray-800 dark:text-gray-200' : 'text-gray-400'}`}>{qty(f.open_position_qty)}</td>
                  <td className={`py-1.5 pr-3 text-right tabular-nums ${(f.pending_order_count || 0) > 0 ? 'text-amber-600 dark:text-amber-400 font-semibold' : 'text-gray-400'}`}>{f.pending_order_count || 0}</td>
                  <td className="py-1.5 pr-3 font-mono text-gray-500 dark:text-gray-400">{f.deactivated_on || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2">
        Read-only detection (a daily 7:15 AM ET beat also emails a digest). Nothing is auto-sold —
        disposition is a deliberate manual action.
      </p>
    </div>
  );
}
